import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { MONTH_CHOICES, YEAR_CHOICES } from '../../utils/payrollUtils';
import EmployeeSearchField from '../common/EmployeeSearchField';

const PayrollFilters = ({ searchTerm, filters, filterOptions, onSearchChange, onFilterChange, onClearFilters }) => {
  const monthOptions = filterOptions?.months || MONTH_CHOICES;
  const yearOptions = filterOptions?.years || YEAR_CHOICES;
  const statusOptions = filterOptions?.payment_statuses || ['Pending', 'Approved', 'Rejected', 'On Hold', 'Paid'];

  return (
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                placeholder="Search by employee name or ID"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <EmployeeSearchField
                value={filters.employee}
                emptyValue=""
                onChange={(id) => onFilterChange('employee', id)}
                apiFilters={filters}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Autocomplete
                options={monthOptions}
                value={filters.month || null}
                onChange={(event, newValue) => onFilterChange('month', newValue || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Month" size="small" placeholder="Select month…" />
                )}
                size="small"
                clearOnBlur={false}
                selectOnFocus
                handleHomeEndKeys
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Autocomplete
                options={yearOptions}
                value={filters.year || null}
                onChange={(event, newValue) => onFilterChange('year', newValue || '')}
                renderInput={(params) => <TextField {...params} label="Year" size="small" placeholder="Select year…" />}
                size="small"
                clearOnBlur={false}
                selectOnFocus
                handleHomeEndKeys
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Autocomplete
                options={statusOptions}
                value={filters.payment_status || null}
                onChange={(event, newValue) => onFilterChange('payment_status', newValue || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Payment Status" size="small" placeholder="Select status…" />
                )}
                size="small"
                clearOnBlur={false}
                selectOnFocus
                handleHomeEndKeys
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            <Grid item xs={12} md={1.5}>
              <Button variant="outlined" onClick={onClearFilters} size="small" sx={{ height: '40px' }}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

PayrollFilters.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    employee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    month: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    payment_status: PropTypes.string,
    department: PropTypes.string,
    designation: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  filterOptions: PropTypes.shape({
    months: PropTypes.arrayOf(PropTypes.string),
    years: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    payment_statuses: PropTypes.arrayOf(PropTypes.string),
  }),
  onSearchChange: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};

PayrollFilters.defaultProps = {
  filterOptions: {},
};

export default PayrollFilters;
